import { Button, makeStyles, Theme, Typography } from '@material-ui/core'
import { FileUploadDialog } from 'components/Common/FileUploader/FileUploaderDialog'
import { LoadingContainer } from 'components/Common/LoadingContainer'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { SettingsHeader } from 'components/OrganizationSettings/Components/SettingsTitle'
import { useController } from 'customHooks/useController'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GetProjectEstimationTemplateResponse } from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { useOrganizationContext } from 'utils/useOrganizationContext'
import { ProjectDeliveryEstimationSettingsController } from './ProjectDeliveryEstimationSettings.controller'

const useStyle = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  page: {
    height: '100%',
  },
}))

export const ProjectDeliveryEstimationSettings = () => {
  const { t } = useTranslation()
  const classes = useStyle()
  const { organizationId } = useOrganizationContext()

  const [fileData, setFileData] =
    useState<GetProjectEstimationTemplateResponse>(null)

  const [fileUploadActive, setFileUploadActive] = useState(false)

  const { controller, loading } = useController(
    () => new ProjectDeliveryEstimationSettingsController()
  )

  const getProjectDeliveryEstimationFileData = useCallback(() => {
    controller
      .GetProjectDeliveryEstimationFileData()
      .then(setFileData)
      .catch((e) => ShowException(t('common:error'), e))
  }, [controller, t])

  useEffect(() => {
    getProjectDeliveryEstimationFileData()
  }, [getProjectDeliveryEstimationFileData])

  return (
    <div
      className={classes.page}
      onDragEnter={(e) => {
        if (e.dataTransfer.items[0]?.kind === 'file') {
          setFileUploadActive(true)
        }
      }}
    >
      <SettingsHeader
        title={t(
          'settings:project-delivery-estimation-title',
          'Project Delivery Estimation'
        )}
      >
        <LocalizedTypography
          translationKey="setting:project-delivery-estimation-subtitle"
          variant="body2"
        >
          estimate the delivery time of a project based on the project&apos;s
          working steps
        </LocalizedTypography>
      </SettingsHeader>
      <div className={classes.root}>
        <FileUploadDialog
          isActive={fileUploadActive}
          setIsActive={setFileUploadActive}
          filePostUrl={`api/parties/${organizationId}/settings/projectestimationtemplate`}
          networkLabel="SaveProjectEstimationTemplates"
          onFileUploadSucceed={() => {
            setFileUploadActive(false)
            setTimeout(() => {
              getProjectDeliveryEstimationFileData()
            }, 500)
          }}
          onFileUploadFail={(err) => {
            ShowException(t('common:error'), err)
            setFileUploadActive(false)
          }}
          allowedExtensions={['.xls', '.xlsx']}
          titleTOptions={{
            translationKey: 'settings:project-delivery-estimation-title',
          }}
          successMessageTitleTKey="settings:project-delivery-estimation-title"
          successMessageTKey="settings:project-delivery-estimation-upload-success"
        />

        <LoadingContainer
          loading={loading['GetProjectDeliveryEstimationFileData']}
          noMessage
          variant="loading-text"
        >
          {fileData?.fileName ? (
            <div>
              <Typography>{fileData.fileName}</Typography>
              <Typography variant="caption" color="textSecondary">
                {t('common:last-updated', 'last updated: {{time}}', {
                  time: moment(fileData.lastUpdatedAt).fromNow(),
                })}
              </Typography>
            </div>
          ) : (
            <Typography>
              {t(
                'settings:project-delivery-estimation-no-file',
                'No active file template found. Upload a template file to estimate the minimum delivery time of a project.'
              )}
            </Typography>
          )}
        </LoadingContainer>
        <div className={classes.buttons}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setFileUploadActive(true)}
          >
            upload
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() =>
              controller
                .DownloadProjectDeliveryEstimationFile()
                .catch((e) => ShowException(t('common:error'), e))
            }
          >
            download
          </Button>
          {fileData ? (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                controller
                  .DeleteProjectDeliveryEstimationFile()
                  .catch((e) => ShowException(t('common:error'), e))
                setTimeout(() => {
                  getProjectDeliveryEstimationFileData()
                }, 500)
              }}
            >
              delete
            </Button>
          ) : null}
        </div>

        {/* {error ? <JsonViewer data={error} /> : null} */}
      </div>
    </div>
  )
}
